import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatMoney } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useCategories } from '@/contexts/CategoriesContext';

interface ExpensePieChartProps {
  data: Array<{
    categoryId: string;
    amount: number;
  }>;
  currency: string;
}

// Custom tooltip component (outside main component to avoid render errors)
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: {
      color: string;
    };
  }>;
  currency: string;
  totalExpenses: number;
}

const CustomTooltip = ({
  active,
  payload,
  currency,
  totalExpenses,
}: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    const percentage = ((payload[0].value / totalExpenses) * 100).toFixed(1);
    return (
      <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/95">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {payload[0].name}
        </p>
        <p
          className="text-lg font-bold"
          style={{ color: payload[0].payload.color }}
        >
          {formatMoney(payload[0].value, currency)}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {percentage}% of total
        </p>
      </div>
    );
  }
  return null;
};

export function ExpensePieChart({
  data,
  currency,
}: ExpensePieChartProps) {
  const { getCategoryLabel, getCategoryColor } = useCategories();
  const { t } = useTranslation();

  // Transform data for the chart
  const chartData = data.map((item) => ({
    name: getCategoryLabel(item.categoryId),
    value: item.amount,
    categoryId: item.categoryId,
    color: getCategoryColor(item.categoryId),
  }));

  const totalExpenses = data.reduce((sum, item) => sum + item.amount, 0);

  // Custom label function that Recharts can use
  const renderLabel = (entry: { percent?: number }) => {
    const percent = ((entry.percent ?? 0) * 100).toFixed(0);
    return `${percent}%`;
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-pink-600">
          <span className="text-xl">🥧</span>
        </div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          {t('dashboard.expenseBreakdown')}
        </h3>
      </div>

      {data.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-slate-500 dark:text-slate-400">{t('dashboard.noExpensesYet')}</p>
          <p className="mt-1 text-sm text-slate-400 dark:text-slate-500">
            {t('dashboard.startTracking')}
          </p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={
                  <CustomTooltip
                    currency={currency}
                    totalExpenses={totalExpenses}
                  />
                }
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="mt-4 grid grid-cols-2 gap-2">
            {chartData.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="truncate text-xs text-slate-600 dark:text-slate-400">
                  {entry.name}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}